
import { motion } from 'framer-motion';

interface JobContentProps {
  companyName: string;
  companyLogo: string;
  companyIntro: string;
  description: string;
  requirements: string[];
  benefits: { icon: string; title: string; desc: string }[];
}

export default function JobContent({ companyName, companyLogo, companyIntro, description, requirements, benefits }: JobContentProps) {
  return (
    <div className="flex-1 min-w-0">
      {/* Company Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-7 mb-8 flex items-center gap-6"
      >
        <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-neon-purple/40 flex-shrink-0">
          <img src={companyLogo} alt={companyName} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-white mb-1">{companyName}</h3>
          <p className="text-sm text-gray-400 leading-relaxed">{companyIntro}</p>
        </div>
        <button className="flex-shrink-0 px-5 py-2.5 rounded-xl border border-neon-pink/40 text-neon-pink text-sm font-medium hover:bg-neon-pink/10 transition-all whitespace-nowrap cursor-pointer">
          <i className="ri-add-line mr-1"></i>팔로우
        </button>
      </motion.div>

      {/* Position Description */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-7 mb-8"
      >
        <h4 className="text-lg font-bold text-neon-purple mb-5 flex items-center gap-2">
          <i className="ri-file-text-line w-5 h-5 flex items-center justify-center"></i>
          포지션 소개
        </h4>
        <p className="text-base text-gray-300 leading-[1.9]">{description}</p>
      </motion.div>

      {/* Requirements */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-7 mb-8"
      >
        <h4 className="text-lg font-bold text-neon-purple mb-5 flex items-center gap-2">
          <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center"></i>
          자격요건
        </h4>
        <div className="grid grid-cols-2 gap-3">
          {requirements.map((req, index) => (
            <div
              key={index}
              className="flex items-center gap-3 bg-dark-deep/50 rounded-xl px-5 py-4 border border-white/5"
            >
              <i className="ri-check-line text-emerald-400 text-lg w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm text-gray-300">{req}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Benefits */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-7"
      >
        <h4 className="text-lg font-bold text-neon-purple mb-5 flex items-center gap-2">
          <i className="ri-gift-line w-5 h-5 flex items-center justify-center"></i>
          혜택 및 복지
        </h4>
        <div className="grid grid-cols-3 gap-4">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group bg-dark-deep/50 rounded-xl p-5 border border-white/5 hover:border-neon-purple/30 transition-all duration-300"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-neon-purple/15 mb-4 group-hover:bg-neon-purple/25 transition-colors">
                <i className={`${benefit.icon} text-xl text-neon-purple`}></i>
              </div>
              <h5 className="text-sm font-bold text-white mb-1.5">{benefit.title}</h5>
              <p className="text-xs text-gray-500 leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
